import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import type { AssistantRunEvents } from "../../../api/types/assistantTypes";

interface Options {
  getRunEvents: (runId: string, after: number) => Promise<HandledApiResponse<AssistantRunEvents>>;
  getLastSavedSequence: (runId: string) => number;
  onPage: (page: AssistantRunEvents) => void;
}

interface RunEventsFetcher {
  fetch: (runId: string) => Promise<void>;
}

interface RunFetch {
  again: boolean;
  done: Promise<void>;
}

/** A request for a run made during its fetch runs one more pass instead of a parallel one. */
export const createRunEventsFetcher = ({ getRunEvents, getLastSavedSequence, onPage }: Options): RunEventsFetcher => {
  const runFetches = new Map<string, RunFetch>();

  const fetch = (runId: string): Promise<void> => {
    const inFlight = runFetches.get(runId);
    if (inFlight) {
      inFlight.again = true;
      return inFlight.done;
    }

    let after = getLastSavedSequence(runId);
    const runFetch: RunFetch = { again: true, done: Promise.resolve() };
    const fetchUntilCurrent = async () => {
      try {
        while (runFetch.again) {
          runFetch.again = false;
          const [page, error] = await getRunEvents(runId, after);
          if (error) {
            logError(error, "[runEventsFetcher] getAssistantRunEvents");
            return;
          }
          after = page.events.reduce((last, event) => Math.max(last, event.sequence), after);
          onPage(page);
        }
      } finally {
        runFetches.delete(runId);
      }
    };
    runFetches.set(runId, runFetch);
    runFetch.done = fetchUntilCurrent();
    return runFetch.done;
  };

  return { fetch };
};
